import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

function NewTasks() {
    // 🎨 Theme Context and Color Setup
    const { currentTheme } = useContext(ThemeContext);

    // 🎨 GLOBAL COLORS DEFINITION with Theme Context Integration
    const colors = {
        backgroundDark: '#0f0c29', 
        backgroundMedium: '#302b63', 
        backgroundLight: '#24243e',
        textLight: 'rgba(255, 255, 255, 0.8)',
        borderDark: 'rgba(255, 255, 255, 0.2)',
        primary: currentTheme?.primary || '#6366F1',
        secondary: currentTheme?.secondary || '#8B5CF6',
        accent: currentTheme?.accent || '#06B6D4',
        success: '#10B981',
        warning: '#F59E0B',
        danger: '#EF4444',
        gradient: `linear-gradient(135deg, ${currentTheme?.primary || '#6366F1'}, ${currentTheme?.secondary || '#8B5CF6'})`,
        gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
        gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
        gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
    };

    // Styles matching the consistent design
    const styles = {
        container: {
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
            position: 'relative',
            overflowX: 'hidden'
        },
        animatedBackground: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
        },
        floatingOrb: {
            position: 'absolute',
            borderRadius: '50%',
            filter: 'blur(40px)',
            opacity: 0.3,
            animation: 'float 8s ease-in-out infinite'
        },
        orb1: {
            width: '300px',
            height: '300px',
            background: colors.primary,
            top: '10%',
            left: '5%',
            animationDelay: '0s'
        },
        orb2: {
            width: '200px',
            height: '200px',
            background: colors.secondary,
            top: '60%',
            right: '10%',
            animationDelay: '2s'
        },
        orb3: {
            width: '250px',
            height: '250px',
            background: colors.accent,
            bottom: '20%',
            left: '15%',
            animationDelay: '4s'
        },
        orb4: {
            width: '180px',
            height: '180px',
            background: colors.success,
            top: '30%',
            right: '20%',
            animationDelay: '6s'
        },
        gridOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
        },
        header: {
            padding: '40px 40px 30px',
            position: 'relative',
            zIndex: 2
        },
        headerContent: {
            maxWidth: '1200px',
            margin: '0 auto'
        },
        greeting: {
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            marginBottom: '15px'
        },
        pageTitle: {
            fontSize: '3rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: 0
        },
        welcomeBadge: {
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '8px 16px',
            borderRadius: '20px',
            border: `1px solid ${colors.borderDark}`,
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        badgeText: {
            color: 'white',
            fontWeight: '600',
            fontSize: '0.9rem'
        },
        badgeRole: {
            background: colors.primary,
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.7rem',
            fontWeight: '600',
            textTransform: 'uppercase'
        },
        pageSubtitle: {
            color: colors.textLight,
            fontSize: '1.2rem',
            margin: 0,
            maxWidth: '600px'
        },
        // ✅ ADDED: User info display style
        userInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginTop: '15px',
            padding: '12px 20px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '12px',
            border: `1px solid ${colors.borderDark}`,
            backdropFilter: 'blur(10px)',
            maxWidth: 'fit-content'
        },
        userIcon: {
            color: colors.accent,
            fontSize: '1.2rem'
        },
        userName: {
            color: 'white',
            fontWeight: '600',
            fontSize: '1rem'
        },
        userRole: {
            color: colors.primary,
            fontWeight: '700',
            fontSize: '0.9rem',
            textTransform: 'capitalize'
        },
        messageAlert: {
            color: 'white',
            padding: '20px',
            borderRadius: '15px',
            margin: '0 40px 30px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        alertIcon: {
            fontSize: '1.5rem'
        },
        alertMessage: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            color: 'white'
        },
        alertClose: {
            background: 'none',
            border: 'none',
            color: colors.danger,
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '8px',
            transition: 'background 0.3s ease'
        },
        content: {
            padding: '0 40px 40px',
            maxWidth: '1200px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 2
        },
        formSection: {
            marginBottom: '40px'
        },
        formCard: {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '25px',
            border: `1px solid ${colors.borderDark}`,
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
        },
        cardHeader: {
            background: colors.gradient,
            color: 'white',
            padding: '25px 30px'
        },
        cardTitle: {
            fontSize: '1.8rem',
            fontWeight: '700',
            margin: 0
        },
        cardBody: {
            padding: '40px'
        },
        formGroup: {
            marginBottom: '30px'
        },
        formLabel: {
            color: 'white',
            fontWeight: '600',
            fontSize: '1rem',
            marginBottom: '10px',
            display: 'block'
        },
        formInput: {
            background: 'rgba(255, 255, 255, 0.1)',
            border: `2px solid ${colors.borderDark}`,
            borderRadius: '12px',
            padding: '15px 20px',
            color: 'white',
            fontSize: '1rem',
            width: '100%',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)'
        },
        formSelect: {
            background: 'rgba(255, 255, 255, 0.1)',
            border: `2px solid ${colors.borderDark}`,
            borderRadius: '12px',
            padding: '15px 20px',
            color: 'white',
            fontSize: '1rem',
            width: '100%',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23ffffff' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 15px center',
            backgroundSize: '12px'
        },
        formText: {
            color: colors.textLight,
            fontSize: '0.85rem',
            marginTop: '8px'
        },
        formRow: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
        },
        submitBtn: {
            color: 'white',
            border: 'none',
            padding: '18px 30px',
            borderRadius: '15px',
            fontWeight: '700',
            fontSize: '1.1rem',
            width: '100%',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
        },
        spinner: {
            width: '20px',
            height: '20px',
            border: '2px solid transparent',
            borderTop: '2px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }
    };
    
    // --- Modal Styles ---
    const modalStyles = {
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)'
        },
        modal: {
            background: colors.backgroundMedium,
            padding: '30px',
            borderRadius: '20px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            border: `1px solid ${colors.primary}`,
        },
        header: {
            color: 'white',
            marginBottom: '20px',
            fontSize: '1.5rem',
            fontWeight: '700'
        },
        input: {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderColor: colors.primary,
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '15px',
            marginTop: '25px'
        },
        button: {
            padding: '10px 20px',
            borderRadius: '10px',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            transition: 'background 0.3s ease'
        },
        cancelButton: {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: colors.textLight,
            border: `1px solid ${colors.borderDark}`,
        },
        saveButton: {
            background: colors.gradient,
            color: 'white'
        }
    };
    // --- End Modal Styles ---


    // --- NewClientModal Component ---
    const NewClientModal = ({ show, onClose, onSave, styles, modalStyles }) => {
        const [clientName, setClientName] = useState('');

        if (!show) return null;

        const handleSave = () => {
            if (clientName.trim()) {
                onSave(clientName.trim());
                setClientName('');
            }
        };

        return (
            <div style={modalStyles.backdrop}>
                <div style={modalStyles.modal}>
                    <h4 style={modalStyles.header}>Enter Custom Client Name</h4>
                    <div style={styles.formGroup}>
                        <label htmlFor="new_client_name" style={styles.formLabel}>
                            Client Name * (Will be saved as "Name (Custom)")
                        </label>
                        <input
                            type="text"
                            id="new_client_name"
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            placeholder="e.g., Qasim Ghumman"
                            required
                            style={{ ...styles.formInput, ...modalStyles.input }}
                        />
                    </div>
                    <div style={modalStyles.actions}>
                        <button onClick={onClose} style={{ ...modalStyles.button, ...modalStyles.cancelButton }}>
                            Cancel
                        </button>
                        <button 
                            onClick={handleSave} 
                            disabled={!clientName.trim()}
                            style={{ 
                                ...modalStyles.button, 
                                ...modalStyles.saveButton,
                                opacity: clientName.trim() ? 1 : 0.6,
                                cursor: clientName.trim() ? 'pointer' : 'not-allowed'
                            }}>
                            Save & Use
                        </button>
                    </div>
                </div>
            </div>
        );
    };
    // --- END NewClientModal Component ---


    const [formData, setFormData] = useState({
        raw_link: '',
        video_name: '',
        video_count: '',
        video_type: '',
        reference_video_link: '',
        client_id: '',
        shoot_date: '',
        deadline_date: '',
        notes: ''
    });
    const [clients, setClients] = useState([]);
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // --- New States for New Client Feature ---
    const [showNewClientModal, setShowNewClientModal] = useState(false);
    const [newClientName, setNewClientName] = useState(''); 
    // --- End New States ---

    // ✅ ADDED: State to store logged-in user info
    const [loggedInUser, setLoggedInUser] = useState({
        name: '',
        role: '',
        displayName: ''
    });


    // Video type options
    const videoTypeOptions = [
        { value: '', label: 'Select video type...' },
        { value: 'indoor_reel', label: 'Indoor Reel' },
        { value: 'outdoor_reel', label: 'Outdoor Reel' },
        { value: 'podcast', label: 'Podcast' },
        { value: 'indoor_long_video', label: 'Indoor Long Video' },
        { value: 'outdoor_long_video', label: 'Outdoor Long Video' }
    ];

    useEffect(() => {
        fetchClients();
        // ✅ ADDED: Get logged-in user info on component mount
        getLoggedInUserInfo();
    }, []);

    // ✅ ADDED: Function to get logged-in user info
    const getLoggedInUserInfo = () => {
        const userName = sessionStorage.getItem('userName') || 'User';
        const userRole = sessionStorage.getItem('userRole') || '';
        
        let displayName = userName;
        if (userRole && (userRole.toLowerCase() === 'admin' || userRole.toLowerCase() === 'manager')) {
            displayName = `${userName} (${userRole.charAt(0).toUpperCase() + userRole.slice(1)})`;
        }
        
        setLoggedInUser({
            name: userName,
            role: userRole,
            displayName: displayName
        });
    };

    const fetchClients = async () => {
        try {
            const response = await fetch(`${API_BASE}/get_clients.php`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // FIX: Filter out clients that contain "(Custom)" in their name for the dropdown
            const filteredClients = data
                .filter(client => !client.name.includes('(Custom)'))
                .map(client => ({
                    ...client,
                    name: client.name.trim() 
                }));
                
            setClients(filteredClients); // Use the filtered list for the dropdown
            
        } catch (error) {
            console.error('Error fetching clients:', error);
            setMessage('Error loading clients list: ' + error.message);
        }
    };

    // --- Handler for Client Dropdown Change ---
    const handleClientChange = (e) => {
        const { value } = e.target;
        if (value === "0") {
            // Special value for "Add New Client" is selected
            setShowNewClientModal(true);
            setFormData(prevState => ({
                ...prevState,
                client_id: "0" // Signal the new client flow
            }));
            setNewClientName(''); // Clear previous name
        } else {
            // Existing client selected
            setFormData(prevState => ({
                ...prevState,
                client_id: value
            }));
            setNewClientName(''); // Clear new client name
        }
    };

    // --- Handler to save new client name from modal ---
    const handleSaveNewClient = (name) => {
        setNewClientName(name); // Set the custom name
        setFormData(prevState => ({
            ...prevState,
            client_id: "0" // Keep ID as 0 to signal custom client name to PHP
        }));
        setShowNewClientModal(false); // Close modal
    };
    
    // --- Handler for all other inputs ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage('');

        if (!formData.client_id) {
            setMessage('Please select a client or add a new one.');
            setIsSubmitting(false);
            return;
        }

        let finalClientId = parseInt(formData.client_id);
        let clientNameToSend = '';
        let isCustom = false;

        if (finalClientId === 0) {
            // Custom Client flow
            if (!newClientName) {
                setMessage('Please enter a name for the custom client.');
                setIsSubmitting(false);
                setShowNewClientModal(true);
                return;
            }
            clientNameToSend = newClientName; // Send the raw name
            isCustom = true;
        } else {
            // Existing Client flow
            const selectedClient = clients.find(client => client.id == formData.client_id);
            if (!selectedClient) {
                 setMessage('Invalid client selected.');
                 setIsSubmitting(false);
                 return;
            }
            clientNameToSend = selectedClient.name;
        }

        // Other required field validations
        if (!formData.raw_link) {
            setMessage('Raw drive link is required');
            setIsSubmitting(false);
            return;
        }

        if (!formData.shoot_date) {
            setMessage('Shoot date is required');
            setIsSubmitting(false);
            return;
        }

        // Get user info from sessionStorage including role
        const operatorId = sessionStorage.getItem('userId') || 1;
        const operatorName = sessionStorage.getItem('userName') || 'Operator';
        const operatorRole = sessionStorage.getItem('userRole') || ''; // Get role from session
        
        // Format operator name with role: "Name (Role)"
        let operatorNameWithRole = operatorName;
        if (operatorRole && (operatorRole.toLowerCase() === 'admin' || operatorRole.toLowerCase() === 'manager')) {
            operatorNameWithRole = `${operatorName} (${operatorRole.charAt(0).toUpperCase() + operatorRole.slice(1)})`;
        }
        
        // Submission Logic
        try {            
            const response = await fetch(`${API_BASE}/tasks.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'create_task',
                    raw_link: formData.raw_link,
                    video_count: formData.video_count,
                    video_name: formData.video_name,
                    video_type: formData.video_type,
                    reference_video_link: formData.reference_video_link,
                    client_id: finalClientId, 
                    client_name: clientNameToSend, 
                    shoot_date: formData.shoot_date,
                    deadline_date: formData.deadline_date || null,
                    notes: formData.notes,
                    operator_id: parseInt(operatorId),
                    operator_name: operatorNameWithRole, // Send formatted name with role
                    operator_role: operatorRole // Send role separately if needed
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                let successMessage = '✅ Task created successfully!';
                if (data.is_custom_client) {
                    successMessage += ` (New Custom Client **${data.client_name_used}** added to client list.)`;
                }
                if (operatorRole) {
                    successMessage += ` Created by: ${operatorNameWithRole}`;
                }

                setMessage(successMessage);
                
                // Clear form data after success
                setFormData({
                    raw_link: '',
                    video_name: '',
                    video_count: '',
                    video_type: '',
                    reference_video_link: '',
                    client_id: '',
                    shoot_date: '',
                    deadline_date: '',
                    notes: ''
                });
                setNewClientName(''); 
                
                // Re-fetch if a new client was successfully added so it appears in the dropdown.
                if (data.is_custom_client) {
                    fetchClients(); 
                }
                
            } else {
                setMessage('❌ ' + (data.message || 'Error creating task'));
            }
        } catch (error) {
            console.error('Error creating task:', error);
            setMessage('❌ Error creating task: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout>
            <div style={styles.container}>
                {/* Animated Background */}
                <div className="animated-background" style={styles.animatedBackground}>
                    <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                    <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                    <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                    <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                    <div className="grid-overlay" style={styles.gridOverlay}></div>
                </div>

                {/* Header Section */}
                <div className="tasks-header" style={styles.header}>
                    <div className="header-content" style={styles.headerContent}>
                        <div className="welcome-section">
                            <div className="greeting" style={styles.greeting}>
                                <h1 className="page-title" style={styles.pageTitle}>
                                    <i className="fas fa-plus-circle me-2"></i>
                                    Create New Task
                                </h1>
                                <div className="welcome-badge" style={styles.welcomeBadge}>
                                    <span className="badge-text" style={styles.badgeText}>Task Creation</span>
                                    <span className="badge-role" style={styles.badgeRole}>New</span>
                                </div>
                            </div>
                            <p className="page-subtitle" style={styles.pageSubtitle}>
                                Fill in the details below to create a new video tracking task
                            </p>
                            
                            {/* ✅ ADDED: Display logged-in user info */}
                            {loggedInUser.displayName && (
                                <div style={styles.userInfo}>
                                    <i className="fas fa-user-circle" style={styles.userIcon}></i>
                                    <div>
                                        <span style={styles.userName}>
                                            Logged in as: 
                                        </span>
                                        {' '}
                                        <span style={styles.userRole}>
                                            {loggedInUser.displayName}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Message Alert */}
                {message && (
                    <div className="message-alert" style={{
                        ...styles.messageAlert,
                        background: message.includes('✅') 
                            ? 'rgba(16, 185, 129, 0.1)' 
                            : 'rgba(220, 53, 69, 0.1)',
                        border: message.includes('✅') 
                            ? `1px solid ${colors.success}`
                            : `1px solid ${colors.danger}`,
                        color: message.includes('✅') 
                            ? colors.success
                            : colors.danger
                    }}>
                        <div className="alert-icon" style={{...styles.alertIcon, 
                            color: message.includes('✅') 
                                ? colors.success
                                : colors.danger
                        }}>
                            <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                        </div>
                        <div className="alert-message" style={styles.alertMessage}>
                            <strong>{message.includes('✅') ? 'Success' : 'Error'}</strong>
                            <span style={{color: 'white'}}>{message.replace('✅ ', '').replace('❌ ', '')}</span>
                        </div>
                        <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}

                {/* Main Content */}
                <div className="tasks-content" style={styles.content}>
                    {/* Task Form Card */}
                    <div className="form-section" style={styles.formSection}>
                        <div className="form-card" style={styles.formCard}>
                            <div className="card-header" style={styles.cardHeader}>
                                <h3 style={styles.cardTitle}>
                                    <i className="fas fa-tasks me-2"></i>
                                    Task Details
                                </h3>
                            </div>
                            <div className="card-body" style={styles.cardBody}>
                                <form onSubmit={handleSubmit}>
                                    
                                    {/* Raw Drive Link */}
                                    <div className="form-group" style={styles.formGroup}>
                                        <label htmlFor="raw_link" className="form-label" style={styles.formLabel}>
                                            <i className="fas fa-link me-2"></i>
                                            Raw Drive Link *
                                        </label>
                                        <input type="url" className="form-input" id="raw_link" name="raw_link" value={formData.raw_link} onChange={handleInputChange} placeholder="https://drive.google.com/..." required style={styles.formInput}/>
                                        <div className="form-text" style={styles.formText}>
                                            Enter the Google Drive or YouTube link for raw footage
                                        </div>
                                    </div>

                                    {/* Video Count Field */}
                                    <div className="form-group" style={styles.formGroup}>
                                        <label htmlFor="video_count" className="form-label" style={styles.formLabel}>
                                            <i className="fas fa-hashtag me-2"></i>
                                            Video Count
                                        </label>
                                        <input type="number" className="form-input" id="video_count" name="video_count" value={formData.video_count} onChange={handleInputChange} placeholder="Enter video count" min="0" style={styles.formInput}/>
                                        <div className="form-text" style={styles.formText}>
                                            Enter the number of videos
                                        </div>
                                    </div>

                                    {/* Video Type Field */}
                                    <div className="form-group" style={styles.formGroup}>
                                        <label htmlFor="video_type" className="form-label" style={styles.formLabel}>
                                            <i className="fas fa-film me-2"></i>
                                            Video Type
                                        </label>
                                        <select className="form-select custom-dropdown" id="video_type" name="video_type" value={formData.video_type} onChange={handleInputChange} style={styles.formSelect}>
                                            {videoTypeOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="form-text" style={styles.formText}>
                                            Select the type of video content
                                        </div>
                                    </div>

                                    {/* Video Name */}
                                    <div className="form-group" style={styles.formGroup}>
                                        <label htmlFor="video_name" className="form-label" style={styles.formLabel}>
                                            <i className="fas fa-film me-2"></i>
                                            Video Name (optional)
                                        </label>
                                        <input type="text" className="form-input" id="video_name" name="video_name" value={formData.video_name} onChange={handleInputChange} placeholder="e.g., Spring Company Tower" style={styles.formInput}/>
                                    </div>

                                    {/* Reference Video Link */}
                                    <div className="form-group" style={styles.formGroup}>
                                        <label htmlFor="reference_video_link" className="form-label" style={styles.formLabel}>
                                            <i className="fas fa-external-link-alt me-2"></i>
                                            Reference Video Link (optional)
                                        </label>
                                        <input type="url" className="form-input" id="reference_video_link" name="reference_video_link" value={formData.reference_video_link} onChange={handleInputChange} placeholder="https://..." style={styles.formInput}/>
                                    </div>

                                    {/* Client, Shoot Date and Deadline Date */}
                                    <div className="form-row" style={styles.formRow}>
                                        <div className="form-group" style={styles.formGroup}>
                                            <label htmlFor="client_id" className="form-label" style={styles.formLabel}>
                                                <i className="fas fa-user-tie me-2"></i>
                                                Client *
                                                {/* Display custom client name if selected/entered */}
                                                {formData.client_id === "0" && newClientName && (
                                                     <span style={{ color: colors.accent, marginLeft: '10px', fontSize: '0.9rem' }}>
                                                         ({newClientName} - Custom)
                                                     </span>
                                                )}
                                            </label>
                                            {/* --- UPDATED Client SELECT --- */}
                                            <select
                                                className="form-input custom-dropdown"
                                                id="client_id"
                                                name="client_id"
                                                value={formData.client_id}
                                                onChange={handleClientChange} 
                                                required
                                                style={styles.formSelect}
                                            >
                                                <option value="">Select client...</option>
                                                {clients.length > 0 ? (
                                                    clients.map(client => (
                                                        <option key={client.id} value={client.id}>
                                                            {client.name}
                                                        </option>
                                                    ))
                                                ) : (
                                                    <option value="" disabled>Loading clients...</option>
                                                )}
                                                {/* --- Custom Field Option (client_id = 0) --- */}
                                                <option value="0" style={{ fontWeight: 'bold', color: colors.success }}>
                                                    + Add Custom Client (Saved in Clients list)
                                                </option>
                                            </select>
                                            <div className="form-text" style={styles.formText}>
                                                {clients.length === 0 ? 'No clients available' : `${clients.length} client(s) available`}
                                            </div>
                                        </div>
                                        
                                        <div className="form-group" style={styles.formGroup}>
                                            <label htmlFor="shoot_date" className="form-label" style={styles.formLabel}>
                                                <i className="fas fa-calendar-alt me-2"></i>
                                                Shoot Date *
                                            </label>
                                            <input type="date" className="form-input" id="shoot_date" name="shoot_date" value={formData.shoot_date} onChange={handleInputChange} required style={styles.formInput}/>
                                        </div>

                                        <div className="form-group" style={styles.formGroup}>
                                            <label htmlFor="deadline_date" className="form-label" style={styles.formLabel}>
                                                <i className="fas fa-clock me-2"></i>
                                                Deadline Date (optional)
                                            </label>
                                            <input type="date" className="form-input" id="deadline_date" name="deadline_date" value={formData.deadline_date} onChange={handleInputChange} style={styles.formInput}/>
                                            <div className="form-text" style={styles.formText}>
                                                Leave empty if no deadline
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div className="form-group" style={styles.formGroup}>
                                        <label htmlFor="notes" className="form-label" style={styles.formLabel}>
                                            <i className="fas fa-sticky-note me-2"></i>
                                            Notes (optional)
                                        </label>
                                        <textarea className="form-input" id="notes" name="notes" value={formData.notes} onChange={handleInputChange} rows="4" placeholder="Any specific requests or instructions..." style={{...styles.formInput, resize: 'vertical'}}></textarea>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="form-group" style={styles.formGroup}>
                                        <button
                                            type="submit"
                                            className="submit-btn"
                                            disabled={isSubmitting || (clients.length === 0 && formData.client_id !== "0" && !newClientName)}
                                            style={{
                                                ...styles.submitBtn,
                                                background: colors.gradient,
                                                opacity: (isSubmitting || (clients.length === 0 && formData.client_id !== "0" && !newClientName)) ? 0.6 : 1,
                                                cursor: (isSubmitting || (clients.length === 0 && formData.client_id !== "0" && !newClientName)) ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner" style={styles.spinner}></span>
                                                    Creating Task...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-plus-circle me-2"></i>
                                                    CREATE TASK
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* New Client Modal */}
            <NewClientModal 
                show={showNewClientModal} 
                onClose={() => {
                    setShowNewClientModal(false);
                    if (!newClientName) {
                        setFormData(prevState => ({...prevState, client_id: ''})); // Revert to empty if canceled and no name entered
                    }
                }}
                onSave={handleSaveNewClient}
                styles={styles} 
                modalStyles={modalStyles} 
            />

            {/* ... (Style blocks remain the same) ... */}
            <style jsx>{`
                .spinning {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes float {
                    0%, 100% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                }
                .form-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
                }
                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
                }
                .form-input:focus {
                    outline: none;
                    border-color: ${colors.primary};
                    box-shadow: 0 0 0 3px ${colors.primary}20;
                }
                .form-select:focus {
                    outline: none;
                    border-color: ${colors.primary};
                    box-shadow: 0 0 0 3px ${colors.primary}20;
                }
                .user-info-display {
                    animation: fadeIn 0.5s ease-in;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            <style jsx global>{`
                .custom-dropdown {
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    background-image: url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23ffffff' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>");
                    background-repeat: no-repeat;
                    background-position: right 15px center;
                    background-size: 12px;
                }
                .custom-dropdown option {
                    background-color: #302b63;
                    color: white;
                    padding: 12px;
                }
                .custom-dropdown:focus {
                    background-image: url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%236366F1' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>");
                }
                .custom-dropdown::-webkit-scrollbar {
                    width: 8px;
                }
                .custom-dropdown::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 4px;
                }
                .custom-dropdown::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.3);
                    border-radius: 4px;
                }
                .custom-dropdown::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.5);
                }
                .custom-dropdown {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
                }
            `}</style>
        </Layout>
    );
}

export default NewTasks;